import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Printer, Mail } from 'lucide-react';

const DevisView = () => {
  const { id } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  // Add print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          size: A4;
          margin: 15mm;
        }
        
        body {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        
        .no-print {
          display: none !important;
        }
        
        .print\\:shadow-none {
          box-shadow: none !important;
        }
        
        .print\\:rounded-none {
          border-radius: 0 !important;
        }
        
        /* Reduce padding for print */
        .p-8 {
          padding: 20px !important;
        }
        
        /* Ensure table fits */
        table {
          page-break-inside: avoid;
        }
        
        /* Prevent page breaks in sections */
        .border-b {
          page-break-after: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    loadDevis();
  }, [id]);

  const loadDevis = async () => {
    try {
      const res = await axios.get(`${API}/devis/${id}`);
      setDevis(res.data);
    } catch (error) {
      console.error('Error loading devis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatPrice = (price, discountPercent = 0) => {
    if (discountPercent === 100) return 'OFFERT';
    return `${price?.toLocaleString() || 0}€`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!devis) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p>Devis introuvable</p>
      </div>
    );
  }

  const uniqueDates = [...new Set(devis.dates?.map(d => d.date) || [])];
  const dateDisplay = uniqueDates.length > 1 ? uniqueDates.map(formatDate).join(' & ') : formatDate(uniqueDates[0]);
  const lieuxDisplay = [...new Set(devis.dates?.map(d => d.lieu) || [])].join(', ');

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4">
      {/* Actions bar */}
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between no-print">
        <Link to="/" className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Imprimer
          </Button>
        </div>
      </div>

      {/* Document */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none print:rounded-none">
        {/* Header */}
        <div className="p-8 border-b flex justify-between items-start">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
              alt="Kolnoa" 
              className="h-16 mb-4"
            />
            <p className="text-sm text-stone-600">99, Rue de Crimée, 75019 Paris</p>
            <p className="text-sm text-stone-600">06.68.11.93.43 | kolnoaprod@gmail.com</p>
            <p className="text-sm text-stone-500">SIRET: 51171274700019</p>
          </div>
          <div className="text-right">
            <h1 className="text-2xl font-bold text-stone-800">
              {devis.document_type === 'facture' ? 'FACTURE' : 'DEVIS'} N° {devis.numero}
            </h1>
            <p className="text-stone-600">Date: {formatDate(devis.created_at)}</p>
          </div>
        </div>

        {/* Client & Event Info */}
        <div className="p-8 grid md:grid-cols-2 gap-8 border-b bg-stone-50">
          <div>
            <h2 className="font-semibold text-stone-800 mb-2">CLIENT</h2>
            <p className="font-medium">{devis.client_info?.prenom} {devis.client_info?.nom}</p>
            <p className="text-sm text-stone-600">{devis.client_info?.adresse}</p>
            <p className="text-sm text-stone-600">{devis.client_info?.ville}</p>
            <p className="text-sm text-stone-600">{devis.client_info?.email}</p>
            <p className="text-sm text-stone-600">{devis.client_info?.telephone}</p>
          </div>
          <div>
            <h2 className="font-semibold text-stone-800 mb-2">ÉVÉNEMENT</h2>
            <p className="font-medium capitalize">
              {devis.event_info?.event_type?.replace('_', ' ')} de {devis.event_info?.vedettes}
            </p>
            <p className="text-sm text-stone-600">Date: {dateDisplay}</p>
            <p className="text-sm text-stone-600">Lieux: {lieuxDisplay}</p>
          </div>
        </div>

        {/* Prestations Table */}
        <div className="p-8">
          <table className="w-full document-table mb-8">
            <thead>
              <tr className="bg-stone-800 text-white">
                <th className="text-left py-3 px-4">PRESTATION</th>
                <th className="text-left py-3 px-4">DÉTAILS</th>
                <th className="text-right py-3 px-4">PRIX HT</th>
                <th className="text-right py-3 px-4">REMISE</th>
                <th className="text-right py-3 px-4">TOTAL HT</th>
              </tr>
            </thead>
            <tbody>
              {devis.lines?.map((line, i) => {
                const lineTotal = line.price_ht * (line.quantity || 1) * (1 - (line.discount_percent || 0) / 100);
                return (
                  <tr key={i} className="border-b">
                    <td className="py-3 px-4 font-medium">{line.prestation_name}</td>
                    <td className="py-3 px-4 text-sm text-stone-600">{line.description}</td>
                    <td className="py-3 px-4 text-right">{line.price_ht}€</td>
                    <td className="py-3 px-4 text-right text-amber-600">
                      {line.discount_percent > 0 ? `-${line.discount_percent}%` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {formatPrice(Math.round(lineTotal * 100) / 100, line.discount_percent)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between py-2 border-b">
                <span>Sous-total HT</span>
                <span className="font-medium">{devis.sous_total_ht?.toLocaleString()}€</span>
              </div>
              {devis.discount_total_percent > 0 && (
                <div className="flex justify-between py-2 border-b text-amber-600">
                  <span>Remise ({devis.discount_total_percent}%)</span>
                  <span>-{devis.discount_total_euros?.toLocaleString()}€</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span>Total HT</span>
                <span className="font-medium">{devis.total_ht?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span>TVA ({devis.tva_rate}%)</span>
                <span>{devis.tva_amount?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between py-3 text-lg font-bold bg-stone-100 px-4 -mx-4">
                <span>TOTAL TTC</span>
                <span>{devis.total_ttc?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between py-2 text-amber-700">
                <span>Acompte ({devis.acompte_percent}%)</span>
                <span className="font-medium">{devis.acompte_euros?.toLocaleString()}€</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 bg-stone-50 border-t">
          <div className="mb-4">
            <h3 className="font-semibold mb-2">MODALITÉS DE LIVRAISON</h3>
            <p className="text-sm text-stone-600">{devis.livraison_phrase}</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">MODALITÉS DE PAIEMENT</h3>
            <p className="text-sm text-stone-600">
              {devis.acompte_percent}% d'acompte à la commande ({devis.acompte_euros?.toLocaleString()}€), le solde le jour de l'événement.
            </p>
          </div>
          <p className="text-xs text-stone-400 mt-6">TVA non applicable, art. 293 B du CGI.</p>
        </div>
      </div>
    </div>
  );
};

export default DevisView;
